import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Dumbbell, Play, RotateCcw } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import ExercisesDropdown from "../components/ExercisesDropdown"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table"
import { apiFetch } from "../utils/apiFetch"
import { generateSmolovJrProgram, type SmolovJrSession } from "../utils/smolovJr"

export const Route = createFileRoute("/_app/smolov-jr")({
  component: SmolovJrPage,
})

function SmolovJrPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [exerciseName, setExerciseName] = useState("")
  const [oneRepMax, setOneRepMax] = useState<number>(0)
  const [weeklyIncrement, setWeeklyIncrement] = useState(5)

  const program = useMemo(() => {
    if (oneRepMax <= 0) {
      return null
    }
    return generateSmolovJrProgram(oneRepMax, weeklyIncrement)
  }, [oneRepMax, weeklyIncrement])

  const week1Sessions = useMemo(() => program?.sessions.filter((s) => s.week === 1) ?? [], [program])

  const week2Sessions = useMemo(() => program?.sessions.filter((s) => s.week === 2) ?? [], [program])

  const week3Sessions = useMemo(() => program?.sessions.filter((s) => s.week === 3) ?? [], [program])

  const createMutation = useMutation({
    mutationFn: async () => {
      const result = await apiFetch<{ programId: string }>("/v1/smolov-jr/create", {
        method: "POST",
        body: JSON.stringify({
          exerciseName,
          oneRepMax,
          weeklyIncrement,
        }),
      })

      await apiFetch(`/v1/programs/${result.programId}/start`, {
        method: "POST",
      })

      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Smolov JR program created and started!")
      navigate({ to: "/programs" })
    },
    onError: () => {
      toast.error("Failed to create Smolov JR program")
    },
  })

  const canSubmit = exerciseName.trim() !== "" && oneRepMax > 0 && weeklyIncrement > 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-2xl">Smolov JR</h1>
          <p className="text-muted-foreground text-sm">3-week squat or bench program based on your 1RM</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Program Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="exercise" className="block">
              Exercise
            </Label>
            <ExercisesDropdown
              value={exerciseName}
              onValueChange={setExerciseName}
              placeholder="Select or type an exercise (e.g. Bench Press, Squat)..."
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="orm" className="block">
                One Rep Max (lbs)
              </Label>
              <Input
                id="orm"
                type="number"
                min={1}
                max={2000}
                value={oneRepMax || ""}
                onChange={(e) => setOneRepMax(Number(e.target.value))}
                placeholder="e.g. 200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="increment" className="block">
                Weekly Increment (lbs)
              </Label>
              <Input
                id="increment"
                type="number"
                min={1}
                max={100}
                value={weeklyIncrement}
                onChange={(e) => setWeeklyIncrement(Number(e.target.value))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {program && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                Program Preview
                <Badge variant="secondary">12 sessions</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <WeekTable week={1} sessions={week1Sessions} />
                <WeekTable week={2} sessions={week2Sessions} />
                <WeekTable week={3} sessions={week3Sessions} />

                <div className="rounded-md border border-dashed p-4 text-center">
                  <p className="font-medium text-sm">Week 4 — Deload</p>
                  <p className="text-muted-foreground text-sm">Rest and recover. Then test your new 1RM.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button size="lg" disabled={!canSubmit || createMutation.isPending} onClick={() => createMutation.mutate()}>
              <Play className="mr-2 h-4 w-4" />
              {createMutation.isPending ? "Creating..." : "Start Smolov JR"}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                setExerciseName("")
                setOneRepMax(0)
                setWeeklyIncrement(10)
              }}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
          </div>
        </>
      )}

      {!program && (
        <Card>
          <CardContent className="py-12 text-center">
            <Dumbbell className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg text-muted-foreground">Enter your exercise and 1RM above to generate the program</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function WeekTable({ week, sessions }: { week: number; sessions: SmolovJrSession[] }) {
  if (sessions.length === 0) {
    return null
  }

  return (
    <div>
      <h3 className="mb-2 font-semibold text-sm">Week {week}</h3>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Day</TableHead>
              <TableHead>Sets × Reps</TableHead>
              <TableHead>% 1RM</TableHead>
              <TableHead className="text-right">Weight</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={`${session.week}-${session.dayIndex}`}>
                <TableCell className="font-medium">{session.day}</TableCell>
                <TableCell>
                  {session.sets} × {session.reps}
                </TableCell>
                <TableCell>{session.percentage * 100}%</TableCell>
                <TableCell className="text-right font-mono">{session.weight} lbs</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
